// Partition primitives — group / dedupe items by structural cell
// identity. Pure column-cell layer with no arquero dependency; this
// module owns "group items by key-tuple" for every consumer (verbs,
// window machinery, dataset summaries).

import { trieGetOrCreate } from '#core/lib/utils/map-trie.ts';

import { type Cell, MaterializedDataset } from '../value.ts';
import { materializedRowCount } from './arquero.ts';
import { cellKey } from './cell-key.ts';

/**
 * Partition any item array into buckets keyed on a tuple of extracted
 * values. Items whose extracted-key tuples are structurally equal
 * (via {@link cellKey}) land in the same bucket; bucket insertion
 * order is preserved (first item encountered wins the slot).
 *
 * SSOT for "group items by per-item keys" — every group-by /
 * partition-by verb (aggregate, pivot, time-bucket, window, distinct,
 * join dedupe) routes through this primitive. Structural equality
 * from `cellKey` means struct cells (e.g. CDF `{space, externalId}`
 * instance refs) collapse correctly across rows instead of hashing
 * by JS reference identity.
 *
 * Empty `extractors` collapses everything into one bucket (the
 * global-rollup convention) regardless of `items.length`. Slot
 * lookup is delegated to {@link trieGetOrCreate} — one Map level per
 * extractor.
 */
export const partitionByKeys = <T>(
  items: readonly T[],
  extractors: readonly ((item: T) => unknown)[],
): readonly { readonly keys: readonly unknown[]; readonly items: readonly T[] }[] => {
  if (extractors.length === 0) {
    return [{ keys: [], items }];
  }
  // Single-extractor fast path (the common group-by case: one `:group-by`
  // column, the bucket-key pass, single-column distinct). A single Map
  // level keyed directly by the cell key skips the per-row `extractors.map`
  // and `rawKeys.map` array allocations the general trie walk pays. `cellKey`
  // is still the key, so structural equality and the type-prefix
  // disambiguation that mixed-type partition levels rely on are unchanged.
  if (extractors.length === 1) {
    const extract = extractors[0] as (item: T) => unknown;
    const byKey = new Map<unknown, { keys: readonly unknown[]; items: T[] }>();
    return items.reduce<{ keys: readonly unknown[]; items: T[] }[]>((groups, item) => {
      const rawKey = extract(item);
      const k = cellKey(rawKey);
      const existing = byKey.get(k);
      if (existing === undefined) {
        const fresh = { keys: [rawKey], items: [item] };
        byKey.set(k, fresh);
        groups.push(fresh);
      } else {
        existing.items.push(item);
      }
      return groups;
    }, []);
  }
  const root = new Map<unknown, unknown>();
  return items.reduce<{ keys: readonly unknown[]; items: T[] }[]>((groups, item) => {
    const rawKeys = extractors.map((extract) => extract(item));
    // `create` fires exactly once per distinct key tuple, in first-seen
    // order — so pushing the fresh group there preserves bucket insertion
    // order without a separate "is this new?" check.
    const group = trieGetOrCreate<{ keys: readonly unknown[]; items: T[] }>(root, rawKeys.map(cellKey), () => {
      const fresh = { keys: rawKeys, items: [] as T[] };
      groups.push(fresh);
      return fresh;
    });
    group.items.push(item);
    return groups;
  }, []);
};

/**
 * Keep the first row per unique tuple of the named columns'
 * structural identities ({@link cellKey} via {@link partitionByKeys}).
 * Schema is preserved; row order is the input order of the kept
 * representatives. Names not on `ds.schema` resolve to empty columns
 * and are treated as the constant-null key.
 *
 * SSOT for "row dedupe by key columns" — used by `distinct` and by
 * the join op's right-side dedupe.
 *
 * The identity axis is subset by the SAME `keepIndices` as the columns,
 * so the kept representatives keep their per-row instance identity;
 * `undefined` (not instance-backed) passes through unchanged.
 */
export const dedupeByKeys = (ds: MaterializedDataset, keyNames: readonly string[]): MaterializedDataset => {
  const indices = Array.from({ length: materializedRowCount(ds) }, (_, i) => i);
  const extractors = keyNames.map((n) => {
    const col = (ds.columns[n] ?? []) as readonly Cell[];
    return (i: number) => col[i] ?? null;
  });
  const keepIndices = partitionByKeys(indices, extractors).map(({ items }) => items[0] as number);
  const keptCols = Object.fromEntries(
    Object.entries(ds.columns).map(([k, cells]) => [k, keepIndices.map((i) => cells[i] as Cell)]),
  );
  const axis = ds.identity;
  const identity = axis === undefined ? undefined : keepIndices.map((i) => axis[i]);
  return new MaterializedDataset(keptCols, ds.schema, identity);
};

/**
 * A group produced by {@link groupRowsBy}: the representative
 * by-key values (used to populate the by-columns of the output row)
 * and the row slice belonging to the group (handed to per-group
 * reducers).
 */
export type RowGroup = {
  readonly representativeRow: Record<string, unknown>;
  readonly rows: readonly Record<string, unknown>[];
};

/**
 * Partition rows by the values of one or more columns. Empty
 * `byKeys` collapses the whole input to one group (global rollup
 * convention — `(aggregate (n (count)))` with no `:by` yields one
 * output row).
 *
 * Thin adapter over {@link partitionByKeys}: extractors read each
 * by-key column from the row record, the structural-equality SSOT in
 * `cellKey` handles Date / struct cells uniformly, and the bucket's
 * raw key values populate the representative row of each group.
 */
export const groupRowsBy = (
  rows: readonly Record<string, unknown>[],
  byKeys: readonly string[],
): readonly RowGroup[] => {
  if (byKeys.length === 0) {
    return [{ representativeRow: {}, rows }];
  }
  const extractors = byKeys.map((k) => (row: Record<string, unknown>) => row[k] ?? null);
  return partitionByKeys(rows, extractors).map(({ keys, items }) => ({
    representativeRow: Object.fromEntries(byKeys.map((k, i) => [k, keys[i]])),
    rows: items,
  }));
};
